import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';

import type { ConversionByOriginRow } from '@control-center/shared';

import { LEAD_ORIGIN_LABELS, formatPct } from '../../../lib/pipeline-helpers';

const BRAND = '#7c3aed'; // var(--brand)
const MUTED = '#a1a1aa'; // var(--muted)

interface ConversionByOriginChartProps {
  rows: ConversionByOriginRow[];
}

interface OriginDatum {
  origin: string;
  label: string;
  rate: number;
  conversions: number;
  total: number;
}

/**
 * Conversão lead→cliente por origem (Story 11.12 AC4) — barras horizontais da
 * taxa de conversão (%). Recharts dentro dos tokens do design system.
 */
export function ConversionByOriginChart({ rows }: ConversionByOriginChartProps): JSX.Element {
  if (rows.length === 0) {
    return (
      <section className="overflow-hidden rounded-[14px] border border-line bg-panel">
        <Header />
        <div className="py-10 text-center text-sm text-muted">
          Sem leads no período pra calcular conversão por origem.
        </div>
      </section>
    );
  }

  const data: OriginDatum[] = rows.map((r) => ({
    origin: r.origin,
    label: LEAD_ORIGIN_LABELS[r.origin],
    rate: r.rate,
    conversions: r.conversions,
    total: r.total,
  }));

  return (
    <section className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <Header />
      <div className="h-72 w-full p-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 4, right: 48, bottom: 4, left: 8 }}
          >
            <XAxis
              type="number"
              stroke={MUTED}
              fontSize={11}
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
            />
            <YAxis dataKey="label" type="category" stroke={MUTED} fontSize={12} width={120} />
            <Tooltip content={<OriginTooltip />} cursor={{ fill: 'rgba(124, 58, 237, 0.08)' }} />
            <Bar dataKey="rate" fill={BRAND} radius={[0, 4, 4, 0]} maxBarSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function Header(): JSX.Element {
  return (
    <header className="flex items-center justify-between border-b border-line px-4 py-3">
      <h3 className="text-sm font-medium text-ink">Conversão por origem</h3>
      <span className="text-xs text-muted">lead → cliente</span>
    </header>
  );
}

function OriginTooltip({ active, payload }: TooltipProps<number, string>): JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0]?.payload as OriginDatum;
  return (
    <div className="rounded-md border border-line bg-panel px-3 py-2 text-xs shadow-md">
      <div className="font-medium text-ink">{item.label}</div>
      <div className="mt-1 tabular-nums text-ink-2">{formatPct(item.rate)} de conversão</div>
      <div className="text-muted tabular-nums">
        {item.conversions} de {item.total} {item.total === 1 ? 'lead' : 'leads'}
      </div>
    </div>
  );
}
